// Serviço para autenticação e validação de assinaturas e Ordinals

interface AuthResponse {
  success: boolean;
  hasAccess: boolean;
  message?: string;
}

/**
 * Envia a assinatura e o endereço para o backend para validação
 * O backend verifica a assinatura e a posse de Ordinals
 */
export async function verifySignatureAndOrdinals(
  address: string,
  message: string,
  signature: string
): Promise<AuthResponse> {
  try {
    // URL do endpoint de autenticação no backend
    // Usar a variável de ambiente para o URL do servidor
    // O endpoint correto é https://multiverso-server.onrender.com/api/auth/verify
    const serverUrl = import.meta.env.VITE_SERVER_URL || 'https://multiverso-server.onrender.com';
    const authEndpoint = `${serverUrl}/api/auth/verify`;

    console.log(`[verifySignatureAndOrdinals] Endpoint de autenticação configurado: ${authEndpoint}`);
    console.log(`[verifySignatureAndOrdinals] Tentando verificar via backend: ${authEndpoint}`);
    console.log(`[verifySignatureAndOrdinals] VITE_SERVER_URL: ${import.meta.env.VITE_SERVER_URL}`);

    try {
      // Enviar dados para o backend
      const response = await fetch(authEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address,
          message,
          signature,
        }),
      });

      console.log(`[verifySignatureAndOrdinals] Backend respondeu com status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Não foi possível ler o corpo da resposta');
        console.error(`[verifySignatureAndOrdinals] Backend respondeu com erro: ${response.status}`, {
          statusText: response.statusText,
          responseBody: errorText
        });
        throw new Error(`Backend responded with status: ${response.status} - ${response.statusText}`);
      }

      // Processar resposta do backend
      const data = await response.json();
      console.log(`[verifySignatureAndOrdinals] Resposta do backend:`, data);
      return data;
    } catch (backendError) {
      console.error(`[verifySignatureAndOrdinals] Erro ao chamar o backend:`, backendError);

      // Verificar se é um erro de CORS ou de rede
      if (backendError instanceof TypeError && backendError.message.includes('Failed to fetch')) {
        console.error(`[verifySignatureAndOrdinals] Erro de rede ou CORS ao chamar o backend. Tentando fallback...`);
      }

      // Em caso de erro, tentar verificar diretamente os Ordinals
      console.log(`[verifySignatureAndOrdinals] Tentando verificar diretamente os Ordinals...`);
      return await simulateBackendVerification(address);
    }
  } catch (error) {
    console.error('Error verifying signature and ordinals:', error);

    // Em caso de erro não tratado, tentar verificar diretamente os Ordinals
    console.log(`[verifySignatureAndOrdinals] Erro não tratado. Tentando verificar diretamente os Ordinals...`);
    return await simulateBackendVerification(address);
  }
}

// Lista de IDs de Ordinals permitidos - definida fora da função para estar disponível em todo o escopo
const ALLOWED_ORDINAL_IDS = [
      "e15a9d0ffa88f39aebe6a0ef099f86ae5e3147dae0cc42106159faa2da8a0c29i0",
      "e15a9d0ffa88f39aebe6a0ef099f86ae5e3147dae0cc42106159faa2da8a0c29i1",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai0",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai1",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai2",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai3",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai4",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai5",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai6",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai7",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai8",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai9",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai10",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai11",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai12",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai13",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai14",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai15",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai16",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai17",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai18",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai19",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai20",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai21",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai22",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai23",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai24",
      "3b4699ee187ff6a2b8687887456a2747c0e344a94c1ee7637076ff64d984debai25",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi0",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi1",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi2",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi3",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi4",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi5",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi6",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi7",
      "c5027efa65455f193c8206a69eaadd571b0adbd50882ca3fa3d462e3cbf6952bi8",
      "f78ea8bbda84aef55c81364c8d851ab3987650d65454528d6be67bdfb55cf42ci0",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei0",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei1",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei2",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei3",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei4",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei5",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei6",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei7",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei8",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei9",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei10",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei11",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei12",
      "5b602ef2f789beda1602ee12ca564fce60b8bafb332e782b798a979bef00ca8ei13",
      "f133d374f1337216a11ad1daf61115b16fad7c42b84c1736da9eae707b269ecei0",
      "f133d374f1337216a11ad1daf61115b16fad7c42b84c1736da9eae707b269ecei1",
      "f133d374f1337216a11ad1daf61115b16fad7c42b84c1736da9eae707b269ecei2",
      "f133d374f1337216a11ad1daf61115b16fad7c42b84c1736da9eae707b269ecei3",
      "f133d374f1337216a11ad1daf61115b16fad7c42b84c1736da9eae707b269ecei4",
      "f133d374f1337216a11ad1daf61115b16fad7c42b84c1736da9eae707b269ecei5",
      "899b77ec8b6b5500ca3fada1fefd406b6ebebf8064e2f6f8b301fbb9ed0483bei0",
      "899b77ec8b6b5500ca3fada1fefd406b6ebebf8064e2f6f8b301fbb9ed0483bei1",
      "899b77ec8b6b5500ca3fada1fefd406b6ebebf8064e2f6f8b301fbb9ed0483bei2",
      "899b77ec8b6b5500ca3fada1fefd406b6ebebf8064e2f6f8b301fbb9ed0483bei3",
      "899b77ec8b6b5500ca3fada1fefd406b6ebebf8064e2f6f8b301fbb9ed0483bei4",
      "899b77ec8b6b5500ca3fada1fefd406b6ebebf8064e2f6f8b301fbb9ed0483bei5",
      "9aae4076c1acbb752c798d002a29c8b5af177f22f88d5a17ddee76b0663eef7fi0",
      "96052c2b0109b571aabeb499df9397670e6ac5f5081237b82570209707d5e7e2i0",
      "28d12527eb21ed70a3f48dbafaefe92ef792869b461f589289d63c8220a25debi0",
      "122addae3261689cb2aeea4cb1e991a26396e1b54023a7bfd98ead37c7d6f16fi0",
      "122addae3261689cb2aeea4cb1e991a26396e1b54023a7bfd98ead37c7d6f16fi1",
      "77f4149e8b5e63673b12bf709cadb488521972beb410ebd2a72befc3ef2ff37bi0"
];

/**
 * Função para verificar a posse de Ordinals usando múltiplas APIs
 */
async function simulateBackendVerification(address: string): Promise<AuthResponse> {
  try {
    console.log(`[simulateBackendVerification] Iniciando verificação para endereço: ${address}`);

    // Primeiro, tentar a API Ordiscan com a chave fornecida
    try {
      console.log('[simulateBackendVerification] Tentando API Ordiscan...');

      const ordiscanApiKey = '83244b4a-b94e-43c1-a005-bf0b3dccfece';
      const ordiscanUrl = `https://ordiscan.com/api/v1/address/${address}/inscriptions`;
      console.log(`[simulateBackendVerification] URL da API Ordiscan: ${ordiscanUrl}`);

      const ordiscanResponse = await fetch(ordiscanUrl, {
        headers: {
          'Accept': 'application/json',
          'X-API-KEY': ordiscanApiKey
        }
      });

      console.log(`[simulateBackendVerification] Ordiscan API status: ${ordiscanResponse.status}`);

      if (ordiscanResponse.ok) {
        const ordiscanData = await ordiscanResponse.json();

        // Verificar se a resposta tem o formato esperado
        if (ordiscanData && Array.isArray(ordiscanData)) {
          // Extrair os IDs dos Ordinals
          const userOrdinals = ordiscanData.map((inscription: any) => inscription.id) || [];
          console.log('[simulateBackendVerification] Ordinals do usuário (via Ordiscan API):', userOrdinals);
          console.log('[simulateBackendVerification] Total de Ordinals do usuário (via Ordiscan API):', userOrdinals.length);

          // Verificar se algum dos Ordinals do usuário está na lista de permitidos
          const matchingOrdinals = userOrdinals.filter(ordinal => ALLOWED_ORDINAL_IDS.includes(ordinal));
          const hasOrdinal = matchingOrdinals.length > 0;

          console.log('[simulateBackendVerification] Ordinals permitidos encontrados (via Ordiscan API):', matchingOrdinals);
          console.log('[simulateBackendVerification] Possui Ordinal permitido (via Ordiscan API):', hasOrdinal);

          return {
            success: true,
            hasAccess: hasOrdinal,
            message: hasOrdinal ? 'Access granted (via Ordiscan API)' : 'No allowed Ordinals found (via Ordiscan API)'
          };
        } else {
          console.error('[simulateBackendVerification] Formato de resposta inesperado da API Ordiscan:', ordiscanData);
          // Continuar para o próximo método se o formato não for o esperado
        }
      } else {
        // Registrar detalhes do erro
        const errorText = await ordiscanResponse.text().catch(() => 'Não foi possível ler o corpo da resposta');
        console.error(`[simulateBackendVerification] Ordiscan API respondeu com status: ${ordiscanResponse.status}`, {
          statusText: ordiscanResponse.statusText,
          responseBody: errorText
        });
        // Continuar para o próximo método se a API Ordiscan falhar
      }
    } catch (ordiscanError) {
      console.error('[simulateBackendVerification] Erro na API Ordiscan:', ordiscanError);
      // Continuar para o próximo método se a API Ordiscan falhar
    }

    // Segundo, tentar a API OrdinalSafe
    try {
      console.log('[simulateBackendVerification] Tentando API OrdinalSafe...');

      // Usar CORS proxy para evitar problemas de CORS
      const corsProxy = 'https://corsproxy.io/?';
      const ordinalSafeUrl = `https://api.ordinalsafe.xyz/v1/address/${address}/ordinals`;
      console.log(`[simulateBackendVerification] URL da API OrdinalSafe: ${ordinalSafeUrl}`);

      const response = await fetch(`${corsProxy}${encodeURIComponent(ordinalSafeUrl)}`, {
        headers: {
          'Accept': 'application/json',
        }
      });

      // Log detalhado da resposta
      console.log(`[simulateBackendVerification] OrdinalSafe API status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();

        // Verificar se a resposta tem o formato esperado
        if (data.ordinals && Array.isArray(data.ordinals)) {
          // Extrair os IDs dos Ordinals
          const userOrdinals = data.ordinals.map((ordinal: any) => ordinal.id) || [];
          console.log('[simulateBackendVerification] Ordinals do usuário (via OrdinalSafe API):', userOrdinals);
          console.log('[simulateBackendVerification] Total de Ordinals do usuário (via OrdinalSafe API):', userOrdinals.length);

          // Verificar se algum dos Ordinals do usuário está na lista de permitidos
          const matchingOrdinals = userOrdinals.filter(ordinal => ALLOWED_ORDINAL_IDS.includes(ordinal));
          const hasOrdinal = matchingOrdinals.length > 0;

          console.log('[simulateBackendVerification] Ordinals permitidos encontrados (via OrdinalSafe API):', matchingOrdinals);
          console.log('[simulateBackendVerification] Possui Ordinal permitido (via OrdinalSafe API):', hasOrdinal);

          return {
            success: true,
            hasAccess: hasOrdinal,
            message: hasOrdinal ? 'Access granted (via OrdinalSafe API)' : 'No allowed Ordinals found (via OrdinalSafe API)'
          };
        } else {
          console.error('[simulateBackendVerification] Formato de resposta inesperado da API OrdinalSafe:', data);
          // Continuar para o próximo método se o formato não for o esperado
        }
      } else {
        // Registrar detalhes do erro
        const errorText = await response.text().catch(() => 'Não foi possível ler o corpo da resposta');
        console.error(`[simulateBackendVerification] OrdinalSafe API respondeu com status: ${response.status}`, {
          statusText: response.statusText,
          responseBody: errorText
        });
        // Continuar para o próximo método se a API OrdinalSafe falhar
      }
    } catch (ordinalSafeError) {
      console.error('[simulateBackendVerification] Erro na API OrdinalSafe:', ordinalSafeError);
      // Continuar para o próximo método se a API OrdinalSafe falhar
    }

    // Terceiro, tentar a API Hiro Ordinals como último recurso
    try {
      console.log('[simulateBackendVerification] Tentando API Hiro Ordinals...');

      // Buscar os Ordinals do endereço usando a API do Hiro
      // Usar CORS proxy para evitar problemas de CORS
      const corsProxy = 'https://corsproxy.io/?';
      const hiroUrl = `https://api.hiro.so/ordinals/v1/inscriptions?address=${address}&limit=60`;
      console.log(`[simulateBackendVerification] URL da API Hiro: ${hiroUrl}`);

      const hiroResponse = await fetch(`${corsProxy}${encodeURIComponent(hiroUrl)}`, {
        headers: {
          'Accept': 'application/json',
        }
      });

      // Log detalhado da resposta
      console.log(`[simulateBackendVerification] Hiro API status: ${hiroResponse.status}`);

      if (hiroResponse.ok) {
        // Processar a resposta
        const hiroData = await hiroResponse.json();

        // Verificar se a resposta tem o formato esperado
        if (hiroData.results && Array.isArray(hiroData.results)) {
          // Extrair os IDs dos Ordinals (formato diferente da API Hiro)
          const userOrdinals = hiroData.results.map((inscription: any) => inscription.id) || [];
          console.log('[simulateBackendVerification] Ordinals do usuário (via Hiro API):', userOrdinals);
          console.log('[simulateBackendVerification] Total de Ordinals do usuário (via Hiro API):', userOrdinals.length);

          // Verificar se algum dos Ordinals do usuário está na lista de permitidos
          const matchingOrdinals = userOrdinals.filter(ordinal => ALLOWED_ORDINAL_IDS.includes(ordinal));
          const hasOrdinal = matchingOrdinals.length > 0;

          console.log('[simulateBackendVerification] Ordinals permitidos encontrados (via Hiro API):', matchingOrdinals);
          console.log('[simulateBackendVerification] Possui Ordinal permitido (via Hiro API):', hasOrdinal);

          return {
            success: true,
            hasAccess: hasOrdinal,
            message: hasOrdinal ? 'Access granted (via Hiro API)' : 'No allowed Ordinals found (via Hiro API)'
          };
        } else {
          console.error('[simulateBackendVerification] Formato de resposta inesperado da API Hiro:', hiroData);
          // Continuar para o tratamento de erro final se o formato não for o esperado
        }
      } else {
        // Registrar detalhes do erro
        const errorText = await hiroResponse.text().catch(() => 'Não foi possível ler o corpo da resposta');
        console.error(`[simulateBackendVerification] Hiro API respondeu com status: ${hiroResponse.status}`, {
          statusText: hiroResponse.statusText,
          responseBody: errorText
        });
        // Continuar para o tratamento de erro final se a API Hiro falhar
      }
    } catch (hiroError) {
      console.error('[simulateBackendVerification] Erro na API Hiro:', hiroError);
      // Continuar para o tratamento de erro final se a API Hiro falhar
    }

    // Se chegou até aqui, todas as APIs falharam
    console.log(`[simulateBackendVerification] Todas as APIs falharam. Acesso negado para o endereço: ${address}`);
    return {
      success: false,
      hasAccess: false,
      message: `Verification failed: Unable to verify Ordinals ownership for address ${address.substring(0, 6)}...${address.substring(address.length - 4)}. API services are currently unavailable. Please try again later or contact support if the issue persists.`
    };
  } catch (error) {
    // Tratamento de erro global
    console.error('[simulateBackendVerification] Erro global na verificação:', error);
    return {
      success: false,
      hasAccess: false,
      message: `Verification failed: An unexpected error occurred. Please try again later or contact support if the issue persists.`
    };
  }
}
